import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { formatMonthDay, getWeek } from '@/lib/datetime';
import {
  buildOverrideMap,
  getLessonState,
  isBeforeFirstLesson,
  movedInStartEnd,
  weekdayChar,
} from '@/lib/schedule';
import type { ClassTimes, LessonOverride, LessonOverrideKind, MakeupSchedule } from '@/types';
import { deleteOverride, fetchOverridesForRange, saveOverride } from '@/lib/lessonOverrides';

export interface ScheduleStudent {
  id: string;
  name: string;
  class_days: string[];
  class_times: ClassTimes | null;
  class_start_time: string | null;
  class_end_time: string | null;
  first_lesson_date: string | null;
}

interface WeekScheduleCardProps {
  students: ScheduleStudent[];
}

type ItemKind = 'normal' | 'time' | 'cancel' | 'moved' | 'movedin' | 'makeup';

interface DayItem {
  key: string;
  studentId: string;
  name: string;
  /** 편집 시 기준이 되는 원래 수업 날짜 */
  overrideDate: string;
  kind: ItemKind;
  start: string;
  end: string;
  note: string | null;
  movedToDate: string | null;
  /** 보충 수업(makeup_schedules)일 때의 정보 */
  makeupId?: string;
  makeupDone?: boolean;
}

interface EditingTarget {
  studentId: string;
  studentName: string;
  overrideDate: string;
}

const MODES: { value: LessonOverrideKind; label: string; icon: string }[] = [
  { value: 'time', label: '시간 변경', icon: 'ri-time-line' },
  { value: 'move', label: '날짜 변경', icon: 'ri-calendar-2-line' },
  { value: 'cancel', label: '휴강', icon: 'ri-user-unfollow-line' },
];

function startKey(item: DayItem): string {
  return item.start || '99:99';
}

export default function WeekScheduleCard({ students }: WeekScheduleCardProps) {
  const [weekOffset, setWeekOffset] = useState(0);
  const [overrides, setOverrides] = useState<LessonOverride[]>([]);
  const [makeups, setMakeups] = useState<MakeupSchedule[]>([]);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<EditingTarget | null>(null);
  const [editKind, setEditKind] = useState<LessonOverrideKind>('time');
  const [editStart, setEditStart] = useState('');
  const [editEnd, setEditEnd] = useState('');
  const [editMoveDate, setEditMoveDate] = useState('');
  const [editNote, setEditNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState('');

  const week = useMemo(() => getWeek(weekOffset), [weekOffset]);

  const loadRangeData = useCallback(async () => {
    try {
      const thisWeek = getWeek(0);
      const nextWeek = getWeek(1);
      const from = thisWeek[0].date;
      const to = nextWeek[6].date;
      const [rows, makeupRes] = await Promise.all([
        fetchOverridesForRange(from, to),
        supabase
          .from('makeup_schedules')
          .select('*')
          .gte('scheduled_date', from)
          .lte('scheduled_date', to),
      ]);
      if (makeupRes.error) throw makeupRes.error;
      setOverrides(rows);
      setMakeups((makeupRes.data as MakeupSchedule[]) ?? []);
    } catch {
      setError('수업 일정 정보를 불러오지 못했어요.');
    }
  }, []);

  useEffect(() => {
    loadRangeData();
  }, [loadRangeData]);

  const overrideMap = useMemo(() => buildOverrideMap(overrides), [overrides]);

  const perDay = useMemo(
    () =>
      week.map((w) => {
        const items: DayItem[] = [];

        students.forEach((s) => {
          if (!(s.class_days ?? []).includes(w.day)) return;
          if (isBeforeFirstLesson(s, w.date)) return;
          const state = getLessonState(s, w.day, w.date, overrideMap);
          items.push({
            key: `class-${s.id}`,
            studentId: s.id,
            name: s.name,
            overrideDate: w.date,
            kind: state.kind,
            start: state.start,
            end: state.end,
            note: state.note,
            movedToDate: state.movedToDate,
          });
        });

        // 다른 날짜에서 옮겨온(일정 변경된) 수업
        overrides
          .filter((o) => o.kind === 'move' && o.moved_to_date === w.date)
          .forEach((o) => {
            const s = students.find((x) => x.id === o.student_id);
            if (!s) return;
            const t = movedInStartEnd(s, w.date, o);
            items.push({
              key: `in-${o.id}`,
              studentId: o.student_id,
              name: s.name,
              overrideDate: o.override_date,
              kind: 'movedin',
              start: t.start,
              end: t.end,
              note: o.note,
              movedToDate: null,
            });
          });

        // 추가로 잡은 보충 수업
        makeups
          .filter((m) => m.scheduled_date === w.date)
          .forEach((m) => {
            const s = students.find((x) => x.id === m.student_id);
            if (!s) return;
            items.push({
              key: `mk-${m.id}`,
              studentId: m.student_id,
              name: s.name,
              overrideDate: m.scheduled_date,
              kind: 'makeup',
              start: '',
              end: '',
              note: m.note || m.reason || null,
              movedToDate: null,
              makeupId: m.id,
              makeupDone: Boolean(m.lesson_log_id),
            });
          });

        items.sort((a, b) => startKey(a).localeCompare(startKey(b)));

        const activeCount = items.filter(
          (i) => i.kind === 'normal' || i.kind === 'time' || i.kind === 'movedin' || i.kind === 'makeup',
        ).length;
        const makeupCount = items.filter((i) => i.kind === 'makeup').length;
        const changedCount = items.filter(
          (i) => i.kind !== 'normal' && i.kind !== 'makeup',
        ).length;
        return { ...w, items, activeCount, makeupCount, changedCount };
      }),
    [week, students, overrideMap, overrides, makeups],
  );

  const totalThisWeek = perDay.reduce((acc, d) => acc + d.activeCount, 0);
  const todayCount =
    weekOffset === 0 ? perDay.find((d) => d.isToday)?.activeCount ?? 0 : 0;
  const makeupThisWeek = perDay.reduce((acc, d) => acc + d.makeupCount, 0);
  const changedThisWeek = perDay.reduce((acc, d) => acc + d.changedCount, 0);
  const rangeLabel = `${formatMonthDay(week[0].date)} ~ ${formatMonthDay(week[6].date)}`;

  const openEdit = (item: DayItem) => {
    const o = overrideMap[`${item.studentId}__${item.overrideDate}`];
    setEditing({
      studentId: item.studentId,
      studentName: item.name,
      overrideDate: item.overrideDate,
    });
    setEditKind(o?.kind ?? (item.kind === 'movedin' ? 'move' : 'time'));
    setEditStart(o?.start_time ?? (item.kind === 'movedin' ? '' : item.start));
    setEditEnd(o?.end_time ?? (item.kind === 'movedin' ? '' : item.end));
    setEditMoveDate(o?.moved_to_date ?? (item.kind === 'movedin' ? item.overrideDate : ''));
    setEditNote(o?.note ?? '');
    setEditError('');
  };

  const closeEdit = () => {
    setEditing(null);
    setEditStart('');
    setEditEnd('');
    setEditMoveDate('');
    setEditNote('');
    setEditError('');
  };

  const handleSave = async () => {
    if (!editing) return;
    if (editKind === 'move' && !editMoveDate) {
      setEditError('변경할 날짜를 선택해주세요.');
      return;
    }
    if (editKind === 'time' && !editStart && !editEnd) {
      setEditError('변경할 시작·종료 시간을 입력해주세요.');
      return;
    }
    setSaving(true);
    setEditError('');
    try {
      await saveOverride({
        studentId: editing.studentId,
        date: editing.overrideDate,
        kind: editKind,
        start: editStart,
        end: editEnd,
        movedToDate: editMoveDate,
        note: editNote,
      });
      await loadRangeData();
      closeEdit();
    } catch {
      setEditError('수업 일정 저장에 실패했어요. 잠시 후 다시 시도해주세요.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!editing) return;
    setSaving(true);
    setEditError('');
    try {
      await deleteOverride(editing.studentId, editing.overrideDate);
      await loadRangeData();
      closeEdit();
    } catch {
      setEditError('정규 수업으로 되돌리지 못했어요. 잠시 후 다시 시도해주세요.');
    } finally {
      setSaving(false);
    }
  };

  const editingHasOverride = editing
    ? Boolean(overrideMap[`${editing.studentId}__${editing.overrideDate}`])
    : false;

  const itemTone: Record<ItemKind, string> = {
    normal: 'border-background-200',
    time: 'border-accent-300',
    movedin: 'border-primary-300',
    makeup: 'border-accent-300',
    moved: 'border-dashed border-background-300 opacity-80',
    cancel: 'border-dashed border-background-300 opacity-80',
  };

  return (
    <section className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-primary-700">
            <i className="ri-calendar-schedule-line"></i>
          </span>
          <div>
            <h2 className="text-base font-bold text-foreground-950">주간 수업일정</h2>
            <p className="mt-0.5 text-[11px] text-foreground-500">{rangeLabel}</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex gap-1 rounded-full bg-background-100 p-1">
            <button
              type="button"
              onClick={() => setWeekOffset(0)}
              className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors whitespace-nowrap ${
                weekOffset === 0
                  ? 'bg-primary-500 text-background-50'
                  : 'text-foreground-600 hover:text-foreground-900'
              }`}
            >
              이번 주
            </button>
            <button
              type="button"
              onClick={() => setWeekOffset(1)}
              className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors whitespace-nowrap ${
                weekOffset === 1
                  ? 'bg-primary-500 text-background-50'
                  : 'text-foreground-600 hover:text-foreground-900'
              }`}
            >
              다음 주
            </button>
          </div>
          <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-semibold text-secondary-900">
            수업 {totalThisWeek}건
          </span>
          {weekOffset === 0 && (
            <span className="rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold text-accent-800">
              오늘 {todayCount}건
            </span>
          )}
          {makeupThisWeek > 0 && (
            <span className="flex items-center gap-1 rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold text-accent-800">
              <i className="ri-calendar-schedule-line"></i>
              보충 {makeupThisWeek}건
            </span>
          )}
          {changedThisWeek > 0 && (
            <span className="flex items-center gap-1 rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-700">
              <i className="ri-equalizer-line"></i>
              일정 변경 {changedThisWeek}건
            </span>
          )}
        </div>
      </div>

      {error && (
        <div className="mt-4 flex items-center gap-2 rounded-md border border-accent-200 bg-accent-100/70 px-3 py-2 text-xs text-accent-800">
          <i className="ri-error-warning-line"></i>
          {error}
        </div>
      )}

      {students.length === 0 || totalThisWeek === 0 ? (
        <div className="mt-5 flex flex-col items-center gap-2 rounded-lg border border-dashed border-background-300 py-10">
          <i className="ri-calendar-line text-2xl text-foreground-400"></i>
          <p className="text-sm text-foreground-600">
            {weekOffset === 1 ? '다음 주에 예정된 수업이 없어요.' : '이번 주에 예정된 수업이 없어요.'}
          </p>
          {weekOffset === 0 && (
            <Link
              to="/students/new"
              className="mt-1 flex items-center gap-1 rounded-md bg-primary-500 px-3.5 py-2 text-xs font-semibold text-background-50 hover:bg-primary-600 whitespace-nowrap"
            >
              <i className="ri-add-line"></i>
              학생 등록하기
            </Link>
          )}
        </div>
      ) : (
        <div className="mt-5 grid grid-cols-2 gap-2.5 sm:grid-cols-4 lg:grid-cols-7">
          {perDay.map((d) => (
            <div
              key={d.day}
              className={`flex min-h-[132px] flex-col rounded-lg border p-3 ${
                d.isToday
                  ? 'border-primary-300 bg-primary-50'
                  : 'border-background-200 bg-background-100'
              }`}
            >
              <div className="flex items-center justify-between">
                <span
                  className={`flex h-7 w-7 items-center justify-center rounded-md text-xs font-bold ${
                    d.isToday
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-200 text-foreground-700'
                  }`}
                >
                  {d.day}
                </span>
                <span className="text-[10px] text-foreground-400">{formatMonthDay(d.date)}</span>
              </div>
              {d.isToday && (
                <span className="mt-1 text-[11px] font-semibold text-primary-700">오늘</span>
              )}

              <div className="mt-2 flex flex-1 flex-col gap-1.5">
                {d.items.length === 0 ? (
                  <p className="mt-1 text-[11px] text-foreground-400">수업 없음</p>
                ) : (
                  d.items.map((item) => (
                    <div
                      key={item.key}
                      className={`rounded-md border bg-background-50 px-2 py-1.5 transition ${itemTone[item.kind]}`}
                    >
                      <div className="flex items-start justify-between gap-1">
                        <Link to={`/students/${item.studentId}`} className="group min-w-0 flex-1">
                          <p className="truncate text-xs font-semibold text-foreground-900 group-hover:text-primary-700">
                            {item.name}
                          </p>
                          {(item.kind === 'moved' || item.kind === 'cancel') ? (
                            <p className="mt-0.5 text-[11px] text-foreground-400">
                              {item.kind === 'moved' ? '다른 날짜로 변경' : '휴강'}
                            </p>
                          ) : item.kind === 'makeup' ? (
                            <p className="mt-0.5 truncate text-[11px] text-foreground-500">
                              {item.note || '보충 수업'}
                            </p>
                          ) : item.start ? (
                            <p
                              className={`mt-0.5 flex items-center gap-0.5 text-[11px] ${
                                item.kind === 'time'
                                  ? 'font-semibold text-accent-700'
                                  : item.kind === 'movedin'
                                    ? 'font-semibold text-primary-700'
                                    : 'text-foreground-500'
                              }`}
                            >
                              <i
                                className={
                                  item.kind === 'movedin'
                                    ? 'ri-calendar-2-line'
                                    : item.kind === 'time'
                                      ? 'ri-equalizer-line'
                                      : 'ri-time-line'
                                }
                              ></i>
                              {item.start}
                              {item.end ? `~${item.end}` : ''}
                            </p>
                          ) : (
                            <p className="mt-0.5 text-[11px] text-foreground-400">시간 미입력</p>
                          )}
                        </Link>
                        {item.kind === 'makeup' ? (
                          <Link
                            to={
                              item.makeupDone
                                ? `/students/${item.studentId}`
                                : `/lessons/new?student=${item.studentId}&makeup=1&date=${item.overrideDate}&schedule=${item.makeupId}`
                            }
                            aria-label={
                              item.makeupDone
                                ? `${item.name} 보충 수업일지 보기`
                                : `${item.name} 보충 수업일지 작성`
                            }
                            className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-foreground-400 transition hover:bg-accent-100 hover:text-accent-700"
                          >
                            <i
                              className={
                                item.makeupDone ? 'ri-check-double-line text-sm' : 'ri-edit-box-line text-sm'
                              }
                            ></i>
                          </Link>
                        ) : (
                          <button
                            type="button"
                            onClick={() => openEdit(item)}
                            aria-label={`${item.name} ${formatMonthDay(item.overrideDate)} 수업 일정 변경`}
                            className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-foreground-400 transition hover:bg-primary-100 hover:text-primary-700"
                          >
                            <i className="ri-equalizer-line text-sm"></i>
                          </button>
                        )}
                      </div>

                      {item.kind === 'time' && (
                        <span className="mt-1 inline-flex items-center gap-0.5 rounded-full bg-accent-100 px-1.5 py-0.5 text-[10px] font-semibold text-accent-800">
                          <i className="ri-equalizer-line"></i>
                          시간 변경
                        </span>
                      )}
                      {item.kind === 'movedin' && (
                        <span className="mt-1 inline-flex items-center gap-0.5 rounded-full bg-primary-100 px-1.5 py-0.5 text-[10px] font-semibold text-primary-700">
                          <i className="ri-calendar-2-line"></i>
                          날짜 변경
                        </span>
                      )}
                      {item.kind === 'makeup' && (
                        <span
                          className={`mt-1 inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                            item.makeupDone
                              ? 'bg-primary-100 text-primary-700'
                              : 'bg-accent-100 text-accent-800'
                          }`}
                        >
                          <i className={item.makeupDone ? 'ri-check-line' : 'ri-calendar-schedule-line'}></i>
                          {item.makeupDone ? '보충 완료' : '보충'}
                        </span>
                      )}
                      {item.kind === 'moved' && item.movedToDate && (
                        <span className="mt-1 inline-flex items-center gap-0.5 rounded-full bg-background-200 px-1.5 py-0.5 text-[10px] font-semibold text-foreground-600">
                          <i className="ri-arrow-right-line"></i>
                          {formatMonthDay(item.movedToDate)} 변경
                        </span>
                      )}
                      {item.kind === 'cancel' && (
                        <span className="mt-1 inline-flex items-center gap-0.5 rounded-full bg-background-200 px-1.5 py-0.5 text-[10px] font-semibold text-foreground-600">
                          <i className="ri-user-unfollow-line"></i>
                          휴강
                        </span>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="mt-5 rounded-xl border border-accent-200 bg-accent-50 p-4 md:p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="flex items-center gap-2 text-sm font-bold text-foreground-950">
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                  <i className="ri-equalizer-line"></i>
                </span>
                {editing.studentName} · {formatMonthDay(editing.overrideDate)} (
                {weekdayChar(editing.overrideDate)}) 수업 일정 변경
              </h3>
              <p className="mt-1 text-xs text-foreground-600">
                결석·사정으로 같은 날 시간만 바꾸거나, 다른 날짜로 옮기거나, 휴강으로 처리할 수
                있어요. (없던 수업을 새로 추가하는 보충은 학생카드 &lsquo;보충 수업&rsquo; 탭에서
                잡아요.)
              </p>
            </div>
            <button
              type="button"
              onClick={closeEdit}
              aria-label="닫기"
              className="flex h-7 w-7 items-center justify-center rounded-md text-foreground-500 transition hover:bg-background-100 whitespace-nowrap"
            >
              <i className="ri-close-line"></i>
            </button>
          </div>

          <div className="mt-4 inline-flex gap-1 rounded-full bg-background-100 p-1">
            {MODES.map((m) => (
              <button
                key={m.value}
                type="button"
                onClick={() => setEditKind(m.value)}
                className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors whitespace-nowrap ${
                  editKind === m.value
                    ? 'bg-accent-500 text-background-50'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                <i className={m.icon}></i>
                {m.label}
              </button>
            ))}
          </div>

          {editKind === 'move' && (
            <div className="mt-4 flex flex-wrap items-end gap-3">
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-foreground-700">변경할 날짜</span>
                <input
                  type="date"
                  value={editMoveDate}
                  onChange={(e) => setEditMoveDate(e.target.value)}
                  className="w-44 rounded-md border border-background-300 bg-background-50 px-2.5 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-foreground-700">시작 시간 (선택)</span>
                <input
                  type="time"
                  value={editStart}
                  onChange={(e) => setEditStart(e.target.value)}
                  className="w-32 rounded-md border border-background-300 bg-background-50 px-2.5 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
                />
              </label>
              <span className="pb-2.5 text-foreground-500">~</span>
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-foreground-700">종료 시간 (선택)</span>
                <input
                  type="time"
                  value={editEnd}
                  onChange={(e) => setEditEnd(e.target.value)}
                  className="w-32 rounded-md border border-background-300 bg-background-50 px-2.5 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
                />
              </label>
            </div>
          )}

          {editKind === 'time' && (
            <div className="mt-4 flex flex-wrap items-end gap-3">
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-foreground-700">시작 시간</span>
                <input
                  type="time"
                  value={editStart}
                  onChange={(e) => setEditStart(e.target.value)}
                  className="w-32 rounded-md border border-background-300 bg-background-50 px-2.5 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
                />
              </label>
              <span className="pb-2.5 text-foreground-500">~</span>
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-foreground-700">종료 시간</span>
                <input
                  type="time"
                  value={editEnd}
                  onChange={(e) => setEditEnd(e.target.value)}
                  className="w-32 rounded-md border border-background-300 bg-background-50 px-2.5 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
                />
              </label>
            </div>
          )}

          {editKind === 'move' && !editStart && !editEnd && (
            <p className="mt-2 flex items-center gap-1 text-xs text-foreground-500">
              <i className="ri-information-line"></i>
              시간을 비워두면 변경한 요일의 정규 시간이 그대로 적용돼요.
            </p>
          )}

          <div className="mt-3">
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-foreground-700">메모 (선택)</span>
              <input
                type="text"
                value={editNote}
                onChange={(e) => setEditNote(e.target.value)}
                maxLength={100}
                placeholder="예: 공휴일이라 일요일 오전으로 변경"
                className="w-full rounded-md border border-background-300 bg-background-50 px-2.5 py-2 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
              />
            </label>
          </div>

          {editError && (
            <div className="mt-3 flex items-center gap-2 rounded-md border border-accent-200 bg-background-50 px-3 py-2 text-xs text-accent-800">
              <i className="ri-error-warning-line"></i>
              {editError}
            </div>
          )}

          <div className="mt-4 flex flex-wrap justify-end gap-2">
            {editingHasOverride && (
              <button
                type="button"
                onClick={handleReset}
                disabled={saving}
                className="flex items-center gap-1.5 rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-semibold text-foreground-700 transition hover:bg-background-100 disabled:opacity-50 whitespace-nowrap"
              >
                <i className="ri-refresh-line"></i>
                정규 수업으로 되돌리기
              </button>
            )}
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition hover:bg-primary-600 disabled:opacity-50 whitespace-nowrap"
            >
              {saving ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  저장 중...
                </>
              ) : (
                <>
                  <i className="ri-check-line"></i>
                  일정 저장
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}